import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { EventWithRecordingViewModel } from "../../models/eventWithRecordingViewModel";
import { mapEventWithRecording } from "../../adapters/eventWithRecordingAdapter";
import { GetRecentEventsParams } from "../../../../common/types/events/params";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { EventsApiResponse } from "../../../../common/types/events/api";
import { GET_RECENT_EVENTS } from "../../../../rest/queries";
import { errorNotification } from "../../../../common/variables/notification";

export const useGetRecentEvents = (params: GetRecentEventsParams) => {
  const skip = !params.customerId;

  const variables = buildEventQueryVariables(params);

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    events: EventsApiResponse;
  }>(GET_RECENT_EVENTS, {
    variables,
    skip,
    fetchPolicy: "cache-first",
    onError: error => {
      console.error("GetRecentEvents:", error);

      errorNotification();
    },
  });

  const events = useMemo<EventWithRecordingViewModel[]>(
    () => data?.events?.data.map(mapEventWithRecording) ?? [],
    [data]
  );

  const lastEvaluatedKey = data?.events?.lastEvaluatedKey;

  const fetchNextPage = async () => {
    if (!lastEvaluatedKey) return;

    try {
      await fetchMore({
        variables: {
          ...variables,
          lastEvaluatedKey: encodeURIComponent(
            JSON.stringify(lastEvaluatedKey)
          ),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            ...fetchMoreResult,
            events: {
              ...fetchMoreResult.events,
              data: [
                ...(prev.events?.data ?? []),
                ...(fetchMoreResult.events?.data ?? []),
              ],
              lastEvaluatedKey: fetchMoreResult.events?.lastEvaluatedKey,
            },
          };
        },
      });
    } catch (e) {
      errorNotification("Error fetching next page of events");

      console.error("Error fetching next page of events:", e);
    }
  };

  return {
    events,
    loading,
    error,
    refetch,
    fetchMore,
    fetchNextPage,
    lastEvaluatedKey,
  } as const;
};
